import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  fetchHeros,
  createHero,
  updateHero,
  deleteHero,
  resetDetail,
  resetSave,
} from "@/store/altingross/heros";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Loader2, ImageIcon, ExternalLink,
  TrendingUp, LayoutGrid, CheckCircle2, XCircle, Search, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ─── Sabitler ──────────────────────────────────────────────────────────────
const PAGE_SIZE = 12;
const IMAGE_BASE = "http://localhost:5297/";

const getImageUrl = (imageUrl) =>
  imageUrl ? `${IMAGE_BASE}${imageUrl}` : null;

const EMPTY_FORM = {
  title:       "",
  subtitle:    "",
  description: "",
  buttonText:  "",
  buttonUrl:   "",
  order:       0,
  isActive:    true,
  image:       null,
};

const validationSchema = Yup.object({
  title: Yup.string().required("Başlık zorunlu"),
  order: Yup.number()
    .typeError("Geçerli bir sayı girin")
    .min(0, "Sıra 0 veya daha büyük olmalı")
    .required("Sıra zorunlu"),
});

// ─── Component ─────────────────────────────────────────────────────────────
export default function HeroSliders() {
  const dispatch = useDispatch();
  const { list, saveLoading, deleteLoading, deletingId } = useSelector((s) => s.heros);

  const [modalOpen, setModalOpen]         = useState(false);
  const [deleteTarget, setDeleteTarget]   = useState(null); // { id, title }
  const [editHero, setEditHero]           = useState(null); // hero object being edited
  const [imagePreview, setImagePreview]   = useState(null);
  const [search, setSearch]               = useState("");
  const [page, setPage]                   = useState(1);
  const fileInputRef = useRef(null);

  // ── İlk yükleme ve sayfa değişimi ─────────────────────────────────────
  useEffect(() => {
    dispatch(fetchHeros({ page, size: PAGE_SIZE }));
  }, [dispatch, page]);

  const items      = list.data?.items ?? [];
  const totalPages = list.data?.totalPages ?? 1;
  const totalCount = list.data?.count ?? 0;
  const activeCount   = items.filter((h) => h.isActive).length;
  const inactiveCount = items.filter((h) => !h.isActive).length;

  // ── Arama (client-side filtreleme; API filter da eklenebilir) ──────────
  const filtered = search.trim()
    ? items.filter((h) =>
        h.title?.toLowerCase().includes(search.toLowerCase()) ||
        h.subtitle?.toLowerCase().includes(search.toLowerCase())
      )
    : items;

  // ── Formik ────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: EMPTY_FORM,
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        title:       values.title,
        subtitle:    values.subtitle,
        description: values.description,
        buttonText:  values.buttonText,
        buttonUrl:   values.buttonUrl,
        order:       Number(values.order),
        isActive:    values.isActive,
        image:       values.image,
      };

      try {
        if (editHero) {
          await dispatch(updateHero({ id: editHero.id, data: payload })).unwrap();
          toast.success("Hero güncellendi");
        } else {
          await dispatch(createHero(payload)).unwrap();
          toast.success("Hero oluşturuldu");
        }
        handleClose();
        dispatch(fetchHeros({ page, size: PAGE_SIZE }));
      } catch {
        toast.error(editHero ? "Hero güncellenemedi" : "Hero oluşturulamadı");
      }
    },
  });

  // ── Modal aç/kapat ────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setEditHero(null);
    setImagePreview(null);
    formik.resetForm({ values: EMPTY_FORM });
    dispatch(resetSave());
    setModalOpen(true);
  };

  const handleOpenEdit = (hero) => {
    setEditHero(hero);
    setImagePreview(getImageUrl(hero.imageUrl) || null);
    formik.resetForm({
      values: {
        title:       hero.title       ?? "",
        subtitle:    hero.subtitle    ?? "",
        description: hero.description ?? "",
        buttonText:  hero.buttonText  ?? "",
        buttonUrl:   hero.buttonUrl   ?? "",
        order:       hero.order       ?? 0,
        isActive:    hero.isActive    ?? true,
        image:       null,
      },
    });
    dispatch(resetSave());
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditHero(null);
    setImagePreview(null);
    formik.resetForm({ values: EMPTY_FORM });
    dispatch(resetSave());
    dispatch(resetDetail());
  };

  // ── Resim seçimi ──────────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    formik.setFieldValue("image", file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── Silme ─────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteHero(deleteTarget.id)).unwrap();
      toast.success(`"${deleteTarget.title}" silindi`);
    } catch {
      toast.error("Hero silinemedi");
    }
    setDeleteTarget(null);
  };

  return (
    <div data-testid="hero-page">
      <PageHeader
        testId="hero-header"
        title="Hero Tanımları"
        description="Web sitesi ana sayfasındaki hero slider içeriklerini yönetin."
        action={
          <Button
            onClick={handleOpenCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm shadow-blue-200"
            data-testid="hero-add-button"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Yeni Hero Ekle
          </Button>
        }
      />

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        <KpiCard label="Toplam Hero"  value={totalCount}   icon={LayoutGrid}   accent="blue"  sub="Tüm kayıtlar"     />
        <KpiCard label="Aktif"        value={activeCount}   icon={CheckCircle2} accent="green" sub="Yayında"          />
        <KpiCard label="Pasif"        value={inactiveCount} icon={XCircle}      accent="slate" sub="Yayında değil"    />
      </div>

      {/* Arama */}
      <div className="relative mb-5 max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Başlık veya alt başlık ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-10 bg-white rounded-md"
        />
      </div>

      {/* Grid */}
      {list.loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Yükleniyor...
        </div>
      ) : list.error ? (
        <div className="text-center py-20 text-red-500 text-sm">Veriler yüklenirken hata oluştu.</div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-16 text-center text-slate-400">
          {search ? "Arama sonucu bulunamadı." : "Henüz hero tanımı yok. Yeni ekleyin."}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((hero) => (
            <HeroCard
              key={hero.id}
              hero={hero}
              onEdit={handleOpenEdit}
              onDelete={(id, title) => setDeleteTarget({ id, title })}
              deleteLoading={deleteLoading}
              deletingId={deletingId}
            />
          ))}
        </div>
      )}

      {/* Sayfalama */}
      {totalPages > 1 && !search && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
          <span className="text-xs text-slate-500">
            Sayfa <b className="text-slate-700">{page}</b> / <b className="text-slate-700">{totalPages}</b> — {totalCount} kayıt
          </span>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded-md">
              Önceki
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
              const n = i + 1;
              return (
                <Button key={n} size="sm" variant={n === page ? "default" : "outline"} onClick={() => setPage(n)}
                  className={n === page ? "bg-blue-600 hover:bg-blue-700 rounded-md h-9 w-9 p-0" : "rounded-md h-9 w-9 p-0"}>
                  {n}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-md">
              Sonraki
            </Button>
          </div>
        </div>
      )}

      {/* ── Form Modalı ─────────────────────────────────────────────────── */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editHero ? "Hero Düzenle" : "Yeni Hero Ekle"}</DialogTitle>
            <DialogDescription>
              {editHero
                ? "Hero içeriğini ve resmini güncelleyin."
                : "Yeni bir hero slider öğesi oluşturun."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
              {/* Sol: Resim yükleme */}
              <div className="space-y-3">
                <Label>Görsel</Label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "relative h-52 rounded-lg border-2 border-dashed cursor-pointer overflow-hidden transition-colors",
                    imagePreview ? "border-transparent" : "border-slate-200 hover:border-blue-400 bg-slate-50"
                  )}
                >
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Önizleme" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="text-white text-sm font-medium flex items-center gap-2">
                          <Upload className="h-4 w-4" /> Değiştir
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
                      <ImageIcon className="h-10 w-10" />
                      <span className="text-sm">Resim seçmek için tıklayın</span>
                      <span className="text-xs text-slate-300">PNG, JPG, WEBP</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />

                {/* Sıra + Aktif */}
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <div>
                    <Label htmlFor="order" className="text-xs text-slate-500">Sıra</Label>
                    <Input
                      id="order"
                      name="order"
                      type="number"
                      min={0}
                      value={formik.values.order}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="h-9 mt-1"
                    />
                    {formik.touched.order && formik.errors.order && (
                      <p className="text-xs text-red-500 mt-0.5">{formik.errors.order}</p>
                    )}
                  </div>
                  <div className="flex flex-col justify-end pb-0.5">
                    <Label className="text-xs text-slate-500 mb-2">Aktif</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formik.values.isActive}
                        onCheckedChange={(v) => formik.setFieldValue("isActive", v)}
                      />
                      <span className="text-sm text-slate-600">
                        {formik.values.isActive ? "Yayında" : "Pasif"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sağ: Metin alanları */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="title" className="text-xs text-slate-500">Başlık *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Ana başlık"
                    className="mt-1"
                  />
                  {formik.touched.title && formik.errors.title && (
                    <p className="text-xs text-red-500 mt-0.5">{formik.errors.title}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="subtitle" className="text-xs text-slate-500">Alt Başlık</Label>
                  <Input
                    id="subtitle"
                    name="subtitle"
                    value={formik.values.subtitle}
                    onChange={formik.handleChange}
                    placeholder="Alt başlık"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="text-xs text-slate-500">Açıklama</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    placeholder="Kısa açıklama..."
                    rows={3}
                    className="mt-1 resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="buttonText" className="text-xs text-slate-500">Buton Metni</Label>
                    <Input
                      id="buttonText"
                      name="buttonText"
                      value={formik.values.buttonText}
                      onChange={formik.handleChange}
                      placeholder="Örn: Daha Fazla"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="buttonUrl" className="text-xs text-slate-500">Buton URL</Label>
                    <Input
                      id="buttonUrl"
                      name="buttonUrl"
                      value={formik.values.buttonUrl}
                      onChange={formik.handleChange}
                      placeholder="/hakkimizda"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-5 gap-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={saveLoading}>
                İptal
              </Button>
              <Button
                type="submit"
                disabled={saveLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white min-w-[120px]"
              >
                {saveLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kaydediliyor...</>
                ) : editHero ? "Güncelle" : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Silme onay modalı ───────────────────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hero'yu sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>"{deleteTarget?.title}"</strong> başlıklı hero kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteConfirm}
            >
              {deleteLoading && deletingId === deleteTarget?.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : "Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Hero Kartı ────────────────────────────────────────────────────────────
function HeroCard({ hero, onEdit, onDelete, deleteLoading, deletingId }) {
  return (
    <div className="glass-card overflow-hidden flex flex-col group" data-testid={`hero-card-${hero.id}`}>
      {/* Resim */}
      <div className="relative h-44 bg-slate-100 overflow-hidden shrink-0">
        {getImageUrl(hero.imageUrl) ? (
          <img
            src={getImageUrl(hero.imageUrl)}
            alt={hero.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <ImageIcon className="h-12 w-12" />
          </div>
        )}
        {/* Sıra badge */}
        <div className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2 py-0.5 rounded-full">
          #{hero.order}
        </div>
        {/* Aktif badge */}
        <div className={cn(
          "absolute top-2.5 right-2.5 text-xs font-semibold px-2 py-0.5 rounded-full",
          hero.isActive
            ? "bg-green-500/90 backdrop-blur-sm text-white"
            : "bg-slate-500/80 backdrop-blur-sm text-white"
        )}>
          {hero.isActive ? "Aktif" : "Pasif"}
        </div>
      </div>

      {/* İçerik */}
      <div className="p-4 flex-1 flex flex-col gap-1">
        <h3 className="font-semibold text-slate-900 leading-tight line-clamp-1">{hero.title}</h3>
        {hero.subtitle && (
          <p className="text-sm text-slate-500 line-clamp-1">{hero.subtitle}</p>
        )}
        {hero.description && (
          <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">{hero.description}</p>
        )}
        {hero.buttonText && (
          <div className="mt-auto pt-2">
            <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 text-xs font-medium">
              <ExternalLink className="h-2.5 w-2.5 mr-1" />
              {hero.buttonText}
              {hero.buttonUrl && <span className="ml-1 text-slate-400 font-normal truncate max-w-[80px]">{hero.buttonUrl}</span>}
            </Badge>
          </div>
        )}
      </div>

      {/* Aksiyonlar */}
      <div className="px-4 pb-4 flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-9 rounded-md text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
          onClick={() => onEdit(hero)}
        >
          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Düzenle
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 w-9 p-0 rounded-md text-red-500 hover:bg-red-50 hover:border-red-300"
          onClick={() => onDelete(hero.id, hero.title)}
          disabled={deleteLoading && deletingId === hero.id}
        >
          {deleteLoading && deletingId === hero.id
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Trash2 className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

// ─── KPI Kartı ─────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, accent, sub }) {
  const accentMap = {
    blue:  "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
        <div className={cn("h-9 w-9 rounded-md flex items-center justify-center", accentMap[accent])}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="metric-number text-3xl mt-2">{value}</div>
      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
        <TrendingUp className="h-3 w-3 text-blue-500" />
        {sub}
      </div>
    </div>
  );
}
